import React from "react";
import find from "lodash/find";
import Box from "@mui/material/Box";
import { Page } from "app/components/page";
import { Redirect } from "app/components/redirect";
import { AuthCallback } from "app/pages/auth-callback";
import AccountProfile from "app/pages/account-profile";
import { PageLoader } from "app/components/page-loader";
import { ErrorBoundary } from "app/pages/error-boundary";
import { RouteObject, useParams } from "react-router-dom";
import { COUNTRY_CODELIST } from "app/utils/countryCodelist";
import { AuthenticatedRoute } from "app/router/authenticated-route";
import { UserInvitationCallback } from "app/pages/user-invitation-callback";

const NotFound: React.FC = () => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "calc(100vh - 92px - 256px - 150px)",
    }}
  >
    Page not found
  </Box>
);

const PageWParamRedirectEl: React.FC<{ page: string }> = ({ page }) => {
  const { id } = useParams<{ id: string }>();
  return <Redirect to={`/explorer/${page}/${id}`} />;
};

const CountryRedirectEl: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const iso2 = find(
    COUNTRY_CODELIST,
    (country: any) => country.name === id
  )?.code;
  if (!iso2) {
    return <Redirect to="/explorer/locations" />;
  }
  return <Redirect to={`/explorer/locations/${iso2}`} />;
};

const REDIRECT_ROUTES: RouteObject[] = [
  {
    path: "/app/search-results",
    element: <Redirect to="/explorer" />,
  },
  {
    path: "/app",
    element: <Redirect to="/explorer" />,
  },
  {
    path: "/app/country/:id/:tab?",
    element: <CountryRedirectEl />,
  },
  {
    path: "/app/donor/:id/:tab?",
    element: <Redirect to="/explorer/organisations" />,
  },
  {
    path: "/app/publisher/:id/:tab?",
    element: <PageWParamRedirectEl page="organisations" />,
  },
  {
    path: "/app/organisation/:id/:tab?",
    element: <Redirect to="/explorer/organisations" />,
  },
  {
    path: "/app/activity/:id/:tab?",
    element: <PageWParamRedirectEl page="activities" />,
  },
  {
    path: "/app/data-manager",
    element: <Redirect to="/publisher" />,
  },
  {
    path: "/app/data-manager/bulk-management",
    element: <Redirect to="/publisher" />,
  },
  {
    path: "/app/data-manager/profile-settings",
    element: <Redirect to="/publisher" />,
  },
  {
    path: "/app/validation-report/:orgRef/:uuid/:filename",
    element: <Redirect to="/data-hub" />,
  },
  {
    path: "/app/xml-file/:orgRef/:uuid/:filename",
    element: <Redirect to="/data-hub" />,
  },
  {
    path: "/app/about",
    element: <Redirect to="/about" />,
  },
  {
    path: "/app/faq",
    element: <Redirect to="/faq" />,
  },
  {
    path: "/app/contact",
    element: <Redirect to="/contact" />,
  },
  {
    path: "/app/bookmarks",
    element: <Redirect to="/explore/favourites" />,
  },
  {
    path: "/app/user-settings",
    element: <Redirect to="/account-profile" />,
  },
  {
    path: "/app/user-settings/:id",
    element: <Redirect to="/account-profile" />,
  },
  {
    path: "/knowledgeHub",
    element: <Redirect to="/" />,
  },
  {
    path: "/support",
    element: <Redirect to="/contact" />,
  },
  {
    path: "/services",
    element: <Redirect to="/data-services" />,
  },
  {
    path: "/plans",
    element: <Redirect to="/pricing" />,
  },
  {
    path: "/onboarding/:page?",
    element: <Redirect to="/login" />,
  },
  {
    path: "/",
    element: <Redirect to="/data-services" />,
  },
];

export const ROUTES: RouteObject[] = [
  {
    path: "/",
    element: (
      <React.Suspense fallback={<PageLoader />}>
        <Page />
      </React.Suspense>
    ),
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        path: "/",
        lazy: async () => {
          const Home = await import("app/pages/home");
          return { Component: Home.default };
        },
      },
      {
        path: "/explorer",
        lazy: async () => {
          const Explorer = await import("app/pages/explorer");
          return { Component: Explorer.default };
        },
      },
      {
        path: "/explorer/locations",
        lazy: async () => {
          const ExplorerLocations = await import(
            "app/pages/explorer/pages/locations"
          );
          return { Component: ExplorerLocations.default };
        },
      },
      {
        path: "/explorer/locations/:id",
        lazy: async () => {
          const ExplorerLocation = await import(
            "app/pages/explorer/pages/locations/pages/location"
          );
          return { Component: ExplorerLocation.default };
        },
      },
      {
        path: "/explorer/organisations",
        lazy: async () => {
          const ExplorerOrganisations = await import(
            "app/pages/explorer/pages/organisations"
          );
          return { Component: ExplorerOrganisations.default };
        },
      },
      {
        path: "/explorer/organisations/:id",
        lazy: async () => {
          const ExplorerOrganisation = await import(
            "app/pages/explorer/pages/organisations/pages/organisation"
          );
          return { Component: ExplorerOrganisation.default };
        },
      },
      {
        path: "/explorer/sectors",
        lazy: async () => {
          const ExplorerSectors = await import(
            "app/pages/explorer/pages/sectors"
          );
          return { Component: ExplorerSectors.default };
        },
      },
      {
        path: "/explorer/sectors/:id",
        lazy: async () => {
          const ExplorerSector = await import(
            "app/pages/explorer/pages/sectors/pages/sector"
          );
          return { Component: ExplorerSector.default };
        },
      },
      {
        path: "/explorer/activities",
        lazy: async () => {
          const ExplorerActivities = await import(
            "app/pages/explorer/pages/activities"
          );
          return { Component: ExplorerActivities.default };
        },
      },
      {
        path: "/explorer/activities/:id",
        lazy: async () => {
          const ExplorerActivity = await import(
            "app/pages/explorer/pages/activities/pages/activity"
          );
          return { Component: ExplorerActivity.default };
        },
      },
      {
        path: "/data-services",
        lazy: async () => {
          const ExplorerDataServices = await import("app/pages/data-services");
          return { Component: ExplorerDataServices.default };
        },
      },
      {
        path: "/data-hub",
        lazy: async () => {
          const DataHub = await import("app/pages/data-hub");
          return { Component: DataHub.default };
        },
      },
      {
        path: "/contact",
        lazy: async () => {
          const Contact = await import("app/pages/contact");
          return { Component: Contact.default };
        },
      },
      {
        path: "/validation-report/:id",
        lazy: async () => {
          const ValidationReport = await import("app/pages/validation-report");
          return { Component: ValidationReport.default };
        },
      },
      {
        path: "/privacy-policy",
        lazy: async () => {
          const PrivacyPolicy = await import("app/pages/privacy-policy");
          return { Component: PrivacyPolicy.default };
        },
      },
      {
        path: "/terms-and-conditions",
        lazy: async () => {
          const TermsAndConditions = await import(
            "app/pages/terms-and-conditions"
          );
          return { Component: TermsAndConditions.default };
        },
      },
      {
        path: "/disclaimer",
        lazy: async () => {
          const Disclaimer = await import("app/pages/disclaimer");
          return { Component: Disclaimer.default };
        },
      },
      // Auth
      {
        path: "/login",
        lazy: async () => {
          const Login = await import("app/pages/login");
          return { Component: Login.default };
        },
      },
      {
        path: "/pricing",
        lazy: async () => {
          const Pricing = await import("app/pages/pricing");
          return { Component: Pricing.default };
        },
      },
      {
        path: "/publisher",
        lazy: async () => {
          const Publisher = await import("app/pages/publisher");
          return { Component: Publisher.default };
        },
      },
      {
        path: "/webinar",
        lazy: async () => {
          const WebinarPage = await import("app/pages/webinar");
          return { Component: WebinarPage.default };
        },
      },
      {
        path: "/",
        element: <AuthenticatedRoute />,
        children: [
          {
            path: "/account-profile",
            element: <AccountProfile />,
            children: [
              {
                path: "/account-profile",
                lazy: async () => {
                  const AccountProfileMain = await import(
                    "app/pages/account-profile/components/main"
                  );
                  return { Component: AccountProfileMain.default };
                },
              },
              {
                path: "/account-profile/username",
                lazy: async () => {
                  const UsernameSettings = await import(
                    "app/pages/account-profile/components/username"
                  );
                  return { Component: UsernameSettings.default };
                },
              },
              {
                path: "/account-profile/job-title",
                lazy: async () => {
                  const JobTitleSettings = await import(
                    "app/pages/account-profile/components/job-title"
                  );
                  return { Component: JobTitleSettings.default };
                },
              },
              {
                path: "/account-profile/organisation-information",
                lazy: async () => {
                  const OrgInfoSettings = await import(
                    "app/pages/account-profile/components/organisation-information"
                  );
                  return { Component: OrgInfoSettings.default };
                },
              },
              {
                path: "/account-profile/organisation-members",
                lazy: async () => {
                  const OrgMembersSettings = await import(
                    "app/pages/account-profile/components/organisation-members"
                  );
                  return { Component: OrgMembersSettings.default };
                },
              },
              {
                path: "/account-profile/organisation-members/add",
                lazy: async () => {
                  const OrgMembersAddSettings = await import(
                    "app/pages/account-profile/components/add-member"
                  );
                  return { Component: OrgMembersAddSettings.default };
                },
              },
              {
                path: "/account-profile/connected-accounts",
                lazy: async () => {
                  const ConnectedAccountsSettings = await import(
                    "app/pages/account-profile/components/connected-accounts"
                  );
                  return { Component: ConnectedAccountsSettings.default };
                },
              },
              {
                path: "/account-profile/pricing-plan",
                lazy: async () => {
                  const PricingPlanSettings = await import(
                    "app/pages/account-profile/components/pricing-plan"
                  );
                  return { Component: PricingPlanSettings.default };
                },
              },
              {
                path: "/account-profile/payment-details",
                lazy: async () => {
                  const PaymentSettings = await import(
                    "app/pages/account-profile/components/payment-details"
                  );
                  return { Component: PaymentSettings.default };
                },
              },
              {
                path: "/account-profile/delete",
                lazy: async () => {
                  const AccountDelete = await import(
                    "app/pages/account-profile/components/account-delete"
                  );
                  return { Component: AccountDelete.default };
                },
              },
            ],
          },
          {
            path: "/explorer/favourites",
            lazy: async () => {
              const ExplorerFavourites = await import(
                "app/pages/explorer/pages/favourites"
              );
              return { Component: ExplorerFavourites.default };
            },
          },
        ],
      },
      {
        path: "/account-profile-deleted",
        lazy: async () => {
          const AccountProfileDeleted = await import(
            "app/pages/account-profile/components/account-deleted"
          );
          return { Component: AccountProfileDeleted.default };
        },
      },
      {
        path: "/debug",
        lazy: async () => {
          const DebugPage = await import("app/pages/debug");
          return { Component: DebugPage.default };
        },
      },
      { path: "/invitation/accept", element: <UserInvitationCallback /> },
      { path: "/callback", element: <AuthCallback /> },
      ...REDIRECT_ROUTES,
      // 404
      { path: "*", element: <NotFound /> },
    ],
  },
];
